import {
  Action, ConfirmModal, Portal, Text, useModal,
  useNotifications,
} from '@doveit/bricks';
import React from 'react';
import useProperty from '../../../property/hooks/use-property/useProperty';
import { Status } from '../../../domain/types';
import { Assignment } from '../../../providers/api/dtos';
import { updateAssignment } from '../../../providers/api/assignment/assignmentProvider';
import useAssignmentById from '../../hooks/use-assignment-by-id/useAssignmentById';

export interface ChildFnProps {
  onClick: VoidFunction
}

export interface MarkAssignmentAsCollectedActionProps {
  assignmentId: number;
  children?: (props: ChildFnProps) => React.ReactNode;
  onSuccess?: (updatedAssignment: Assignment) => void;
  onAbort?: VoidFunction;
}

export const MARK_ASSIGNMENT_COLLECTED_SUCCESS_MESSAGE = "Stato dell'immobile aggiornato con successo.";
export const MARK_ASSIGNMENT_COLLECTED_ERROR_MESSAGE = "Non è stato possibile aggiornare lo stato dell'immobile.";

const MarkAssignmentAsCollectedAction: React.FC<MarkAssignmentAsCollectedActionProps> = ({
  assignmentId,
  onSuccess,
  onAbort,
  children,
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const collectedAssignmentModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const { data: assignment, mutate: mutateAssignment } = useAssignmentById(assignmentId);
  const { data: property } = useProperty(assignment?.propertyId);

  const openCollectedAssignmentModal = React.useCallback(() => collectedAssignmentModal.open(), [collectedAssignmentModal]);
  const closeCollectedAssignmentModal = React.useCallback(() => {
    collectedAssignmentModal.close();
    onAbort?.();
  }, [collectedAssignmentModal, onAbort]);

  const onMarkAssignmentAsCollected = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const updatedAssignment = await updateAssignment({
        ...assignment!!,
        isCollected: true,
      });

      setIsSaving(false);
      collectedAssignmentModal.close();

      await mutateAssignment();
      onSuccess?.(updatedAssignment);
      addSuccess(MARK_ASSIGNMENT_COLLECTED_SUCCESS_MESSAGE);
    } catch (_) {
      setIsSaving(false);
      addError(MARK_ASSIGNMENT_COLLECTED_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, assignment, collectedAssignmentModal, mutateAssignment, onSuccess]);

  if (property?.status !== Status.VENDUTO) {
    return null;
  }

  return (
    <>
      {children
        ? children({ onClick: openCollectedAssignmentModal })
        : (
          <Action
            label="Segna come incassato"
            size="S"
            onClick={openCollectedAssignmentModal}
            aria-label="Azione per segnare l'immobile come incassato"
            loading={isSaving}
          />
        )}
      <Portal>
        <ConfirmModal
          {...collectedAssignmentModal}
          title="Sono stati incassati tutti i pagamenti?"
          onConfirm={onMarkAssignmentAsCollected}
          onAbort={closeCollectedAssignmentModal}
          saving={isSaving}
        >
          <Text.Body>
            {`Se tutti i pagamenti relativi all’immobile DV-${property.id} sono stati incassati,
            clicca su "Conferma" per aggiornare automaticamente lo stato dell'immobile a 'Incassato'.`}
          </Text.Body>
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default MarkAssignmentAsCollectedAction;
